import React from 'react';
import { useScrollLock } from '../hooks/useScrollLock';
import { useSheetDrag } from '../hooks/useSheetDrag';

function VolumeBreakdownSheet({ muscleGroup, volumeData, onClose }) {
  useScrollLock(true);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart, handleTouchMove, handleTouchEnd, handleClose } = useSheetDrag(onClose);

  if (!volumeData || !muscleGroup) return null;

  const data = volumeData[muscleGroup];
  if (!data) return null;

  const displayTotal = data.total % 1 === 0 ? data.total : data.total.toFixed(1);

  // Sort exercises by sets (descending)
  const sortedExercises = [...(data.exercises || [])].sort((a, b) => b.sets - a.sets);

  return (
    <div
      className="fixed inset-0 z-50 animate-fade-in"
      style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
      onClick={handleClose}
    >
      <div
        ref={contentRef}
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl"
        style={{ ...sheetStyle, maxHeight: '70vh' }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{muscleGroup}</h2>
            <p className="text-sm text-gray-500">{displayTotal} serie totali</p>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Exercise list */}
        <div className="px-5 pb-8 overflow-y-auto" style={{ maxHeight: 'calc(70vh - 100px)' }}>
          {sortedExercises.length > 0 ? (
            <div className="space-y-3">
              {sortedExercises.map((exercise, idx) => {
                const displaySets = exercise.sets % 1 === 0 ? exercise.sets : exercise.sets.toFixed(1);
                const isSecondary = exercise.contribution && exercise.contribution < 1;

                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-xl ${
                      isSecondary ? 'bg-gray-50' : 'bg-blue-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{exercise.name}</p>
                      {isSecondary ? (
                        <p className="text-xs text-gray-500">
                          Secondario {Math.round(exercise.contribution * 100)}%
                        </p>
                      ) : (
                        <p className="text-xs text-blue-600 font-semibold">Primario 100%</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className={`text-lg font-bold ${isSecondary ? 'text-gray-600' : 'text-blue-600'}`}>
                        {displaySets}
                      </span>
                      <span className="text-sm text-gray-500">serie</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">Nessun esercizio trovato</p>
          )}

          {/* Total footer */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
            <span className="font-semibold text-gray-700">Totale</span>
            <span className="text-xl font-bold text-gray-900">{displayTotal} serie</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VolumeBreakdownSheet;
